import { useEffect, useState } from 'react';

import { toast } from 'react-toastify';

import { Box } from '@mui/material';
import usePricing from 'api/usePricing';
import usePublisher from 'api/usePublisher';
import { EButtonColor, EIntervalsRepeat, EWeekDay } from 'constants/enums';
import {
  dayInMonthOptions,
  intervalsRepeatOptions
} from 'utils/selectOptionsUtils';

import AcSelect from 'components/AcSelect/AcSelect';
import DialogModal from 'components/Dialog/Dialog';

interface ExchangeRateSchedulerModalProps {
  isOpen: boolean;
  setIsExchangeRateSchedulerModalOpen: (open: boolean) => void;
}

const ExchangeRateSchedulerModal: React.FC<ExchangeRateSchedulerModalProps> = ({
  isOpen,
  setIsExchangeRateSchedulerModalOpen
}) => {
  const { updateExchangeRatesScheduler } = usePricing();
  const { getPublisherSettings } = usePublisher();
  const [frequency, setFrequency] = useState<EIntervalsRepeat>(
    EIntervalsRepeat.NONE
  );
  const [hour, setHour] = useState<number>(0);
  const [weeklyFrequencyFactor, setWeeklyFrequencyFactor] = useState<number>(0);
  const [monthlyFrequencyFactor, setMonthlyFrequencyFactor] =
    useState<number>(0);
  const submitUpdateExchangeRatesScheduler = () => {
    updateExchangeRatesScheduler.mutate(
      {
        frequency,
        hour,
        weeklyFrequencyFactor,
        monthlyFrequencyFactor
      },
      {
        onSuccess: () => {
          getPublisherSettings.refetch();
          toast.success('Exchange rates scheduled successfully');
        },
        onError: () => {
          toast.error('Failed to schedule exchange rates');
        },
        onSettled: () => {
          setIsExchangeRateSchedulerModalOpen(false);
        }
      }
    );
  };

  useEffect(() => {
    if (getPublisherSettings.data) {
      setFrequency(
        getPublisherSettings.data.exchangeRatesUpdateScheduler?.frequency ||
          EIntervalsRepeat.NONE
      );
      setHour(
        getPublisherSettings.data.exchangeRatesUpdateScheduler?.hour || 0
      );
      setWeeklyFrequencyFactor(
        getPublisherSettings.data.exchangeRatesUpdateScheduler
          ?.weeklyFrequencyFactor || 0
      );
      setMonthlyFrequencyFactor(
        getPublisherSettings.data.exchangeRatesUpdateScheduler
          ?.monthlyFrequencyFactor || 0
      );
    }
  }, [getPublisherSettings.data]);

  return (
    <DialogModal
      className="exchangeRatesScheduler"
      isOpen={isOpen}
      withDivider={false}
      closeButton={true}
      closeDialog={() => setIsExchangeRateSchedulerModalOpen(false)}
      headline="Schedule Exchange Rates"
      width={'600px'}
      text={`All existing price points will be updated according to the latest
            exchange rate. Players will automatically see the new prices at the
            store`}
      content={
        <ExchangeRateSchedulerContent
          schedulerReapet={frequency}
          setSchedulerRepeat={setFrequency}
          weeklyFrequencyFactor={weeklyFrequencyFactor}
          setWeeklyFrequencyFactor={setWeeklyFrequencyFactor}
          monthlyFrequencyFactor={monthlyFrequencyFactor}
          setMonthlyFrequencyFactor={setMonthlyFrequencyFactor}
          hour={hour}
          setHour={setHour}
        />
      }
      buttons={[
        {
          text: 'Cancel',
          color: EButtonColor.SECONDARY,
          variant: 'outlined',
          func: () => setIsExchangeRateSchedulerModalOpen(false),
          disabled: false,
          fullWidth: false
        },
        {
          text: 'Update',
          color: EButtonColor.PRIMARY,
          variant: 'contained',
          func: () => submitUpdateExchangeRatesScheduler(),
          disabled: false,
          fullWidth: false
        }
      ]}
    />
  );
};

export default ExchangeRateSchedulerModal;

const ExchangeRateSchedulerContent: React.FC<{
  schedulerReapet: EIntervalsRepeat;
  setSchedulerRepeat: (repeat: EIntervalsRepeat) => void;
  weeklyFrequencyFactor?: number;
  setWeeklyFrequencyFactor: (factor: number) => void;
  monthlyFrequencyFactor?: number;
  setMonthlyFrequencyFactor: (factor: number) => void;
  hour?: number;
  setHour: (hour: number) => void;
}> = ({
  schedulerReapet,
  setSchedulerRepeat,
  weeklyFrequencyFactor,
  setWeeklyFrequencyFactor,
  monthlyFrequencyFactor,
  setMonthlyFrequencyFactor,
  hour,
  setHour
}) => {
  return (
    <Box className="m-2 p-8">
      <Box className="flex flex-col gap-4">
        <AcSelect
          header="Repeat"
          items={intervalsRepeatOptions}
          value={schedulerReapet}
          onChange={(e) => {
            setSchedulerRepeat(e.target.value as EIntervalsRepeat);
          }}
        />
        {schedulerReapet !== EIntervalsRepeat.NONE && (
          <Box className="mt-2 flex flex-row gap-2">
            {schedulerReapet !== EIntervalsRepeat.DAILY && (
              <Box style={{ width: '100%' }}>
                {schedulerReapet === EIntervalsRepeat.WEEKLY && (
                  <AcSelect
                    header="Weekday"
                    items={Object.keys(EWeekDay)
                      .filter((k) => isNaN(Number(k)))
                      .map((day, i) => {
                        return {
                          content:
                            day.charAt(0).toUpperCase() +
                            day.slice(1).toLowerCase(),
                          key: i,
                          value: i
                        };
                      })}
                    value={weeklyFrequencyFactor}
                    onChange={(e) => {
                      setWeeklyFrequencyFactor(e.target.value as number);
                    }}
                  />
                )}
                {schedulerReapet === EIntervalsRepeat.MONTHLY && (
                  <AcSelect
                    header="Day of month"
                    items={dayInMonthOptions.map((dayObject, i) => ({
                      ...dayObject,
                      value: i
                    }))}
                    value={monthlyFrequencyFactor}
                    onChange={(e) => {
                      setMonthlyFrequencyFactor(e.target.value as number);
                    }}
                  />
                )}
              </Box>
            )}
            <Box
              style={{
                width: '100%'
              }}
            >
              <AcSelect
                header="Time (UTC)"
                value={hour}
                onChange={(e) => setHour(e.target.value as number)}
                items={Array.from({ length: 24 }, (_, i: number) => ({
                  content: `${i < 10 ? '0' : ''}${i}:00`,
                  key: i,
                  value: i
                }))}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
